import type { ForecastContextSelectedOptions } from "@appretio/context-selector";
import { Chart, Note, SingleSelect, Switch } from "@appretio/ui-kit";
import { useState } from "react";
import { FORECAST_CONTEXT_DIMENSIONS } from "../../forecastContext";
import type { ForecastMetaDataType } from "../../hooks/useFetchForecastMetaData";
import { useFetchStackedData } from "../../hooks/useFetchStackedData";
import { isAlgorithmicProducer } from "../../utils/algorithmicProducer";
import { isPercentageMetric } from "../../utils/percentageMetrics";
import type { ForecastTableControlValues } from "../tableControls/forecastTableControlValues";
import {
  dimensionSelect,
  graphControls,
  stackedNoteWrapper,
  stackedTitleStyle
} from "./ForecastGraph.css";
import { useForecastGraphOptions } from "./hooks/useForecastGraphOptions";
import { useStackedGraphOptions } from "./hooks/useStackedGraphOptions";
import { isStackableMetric } from "./utils/graphUtils";

/** Props for the forecast graph, which renders time-series and stacked charts for selected grid rows. */
interface ForecastGraphProps {
  /** Rows currently selected in the forecast data table. */
  selectedRows: Record<string, unknown>[];
  /** Active time period granularity (WK, MO, QTR, PTG). */
  timePeriod: ForecastTableControlValues["timePeriod"];
  /** Weekly x-axis labels: calendar (via ``gregMapping``) or fiscal keys. */
  weekLabels: ForecastTableControlValues["weekLabels"];
  /** Whether to display accumulated (cumulative) values. */
  accumulated: boolean;
  /** Callback to toggle accumulated mode. */
  onAccumulatedChange: (accumulated: boolean) => void;
  /** Current forecast context selections (brand, location, etc.). */
  context: ForecastContextSelectedOptions;
  /** Resolve the cube identifier that backs a row's ``producer_type`` label. */
  resolveCubeId: (producerType: string) => string | null;
  /** Forecast metadata for metric display names and category mapping. */
  metaData: ForecastMetaDataType | undefined;
  /** True while forecast metadata is loading. */
  isMetaDataFetching: boolean;
  /**
   * Give every series its own auto-scaled y-axis instead of sharing one per
   * category. Only affects the default (non-stacked) chart. Off by default;
   * surfaces wildly different magnitudes (e.g. range-plan stock vs sales).
   */
  axisPerSeries?: boolean;
  /**
   * Axis-grouping mode used with ``axisPerSeries``: ``"per-series"`` (default —
   * one axis per series) or ``"per-category"`` (bucket series onto the shared
   * Units / Price / Value axes, at most three numeric axes + the percentage
   * axis). Keeps the line-style legend either way. The range-plan modal sets
   * ``"per-category"``; the forecast page passes neither.
   */
  axisGrouping?: "per-series" | "per-category";
  /**
   * Show the "Stacked" toggle (and its dimension picker). On by default.
   * Consumers that can't satisfy the stacked path — e.g. the range-plan
   * modal, which passes ``resolveCubeId => null`` so stacking is always
   * inert — set this ``false`` to hide the dead control.
   */
  showStackedControl?: boolean;
}

export function ForecastGraph({
  selectedRows,
  timePeriod,
  weekLabels,
  accumulated,
  onAccumulatedChange,
  context,
  resolveCubeId,
  metaData,
  isMetaDataFetching,
  axisPerSeries = false,
  axisGrouping = "per-series",
  showStackedControl = true
}: ForecastGraphProps) {
  const [stacked, setStacked] = useState(false);
  const [dimension, setDimension] = useState<string | undefined>(undefined);

  // --- Default (non-stacked) chart ---
  const defaultOptions = useForecastGraphOptions({
    selectedRows,
    timePeriod,
    accumulated,
    metaData,
    weekLabels,
    axisPerSeries,
    axisGrouping
  });

  // --- Stacked mode state ---
  const selectedRow =
    stacked && selectedRows.length === 1 ? selectedRows[0] : null;
  const metricName = String(selectedRow?.metric_name ?? "");
  const fiscalYear = String(selectedRow?.fiscal_year_name ?? "");
  const producerType = String(selectedRow?.producer_type ?? "");
  const cubeId = producerType ? resolveCubeId(producerType) : null;
  const stackable = metricName ? isStackableMetric(metricName) : true;
  const isPercentage = isPercentageMetric(
    metricName,
    metaData?.metricFormatMapping
  );

  const stackedEnabled =
    stacked &&
    selectedRows.length === 1 &&
    stackable &&
    !isPercentage &&
    !!dimension &&
    !!cubeId;

  const { data: stackedData, isFetching: stackedFetching } =
    useFetchStackedData(
      stackedEnabled
        ? {
            context: context as Record<string, unknown>,
            cubeId: cubeId!,
            dimension: dimension!,
            metricName,
            fiscalYear
          }
        : null,
      stackedEnabled
    );

  const stackedOptions = useStackedGraphOptions({
    data: stackedData,
    metricName,
    metaData,
    weekLabels
  });

  // --- Stacked mode validation messages ---
  let stackedWarning: string | null = null;
  if (stacked) {
    if (selectedRows.length !== 1) {
      stackedWarning = "Select exactly one row for stacked view";
    } else if (isAlgorithmicProducer(producerType)) {
      // No cube backs an algorithmic row, so there is nothing to stack by.
      stackedWarning =
        "Stacked view is not available for algorithmic forecasts";
    } else if (!stackable) {
      stackedWarning = "Stacked view is not available for this metric";
    } else if (isPercentage) {
      stackedWarning = "Stacked view is not available for percentage metrics";
    }
  }

  // --- Stacked title ---
  const stackedTitle =
    stacked && stackedEnabled && dimension
      ? `${metaData?.metricDisplayMapping[metricName] ?? metricName} by ${FORECAST_CONTEXT_DIMENSIONS.find((o) => o.value === dimension)?.label ?? dimension}`
      : null;

  const showStackedChart = stackedEnabled && !stackedWarning;
  const chartOptions = showStackedChart ? stackedOptions : defaultOptions;
  const loading = showStackedChart ? stackedFetching : isMetaDataFetching;

  const selectedDimensionOption = dimension
    ? FORECAST_CONTEXT_DIMENSIONS.find((o) => o.value === dimension)
    : undefined;

  return (
    <>
      <div className={graphControls}>
        <Switch
          checked={accumulated}
          onChange={onAccumulatedChange}
          label="Accumulated"
          disabled={stacked}
        />
        {showStackedControl && (
          <Switch checked={stacked} onChange={setStacked} label="Stacked" />
        )}
        {showStackedControl && stacked && (
          <div className={dimensionSelect}>
            <SingleSelect
              options={FORECAST_CONTEXT_DIMENSIONS}
              selectedOption={selectedDimensionOption}
              onChange={setDimension}
              placeholder="Dimension"
              size="sm"
            />
          </div>
        )}
      </div>
      {stackedTitle && <div className={stackedTitleStyle}>{stackedTitle}</div>}
      {stackedWarning && (
        <div className={stackedNoteWrapper}>
          <Note text={stackedWarning} variant="warning" size="compact" />
        </div>
      )}
      <Chart options={chartOptions} isLoading={loading} />
    </>
  );
}
